import type { Answers, Bank, Question } from './types';

export function typeName(t: string) {
  return ({ single: '单选题', multiple: '多选题', blank: '填空题' } as Record<string, string>)[t] || t;
}

export function typeTag(t: string) {
  return ({ single: '单选', multiple: '多选', blank: '填空' } as Record<string, string>)[t] || t;
}

// 规范化填空答案（去空格、统一大小写）
export function norm(s: unknown) {
  return String(s == null ? '' : s).trim().replace(/\s+/g, '').toUpperCase();
}

export function checkAnswer(q: Question, userAnswer: string) {
  if (q.type === 'single') {
    return userAnswer === norm(q.answer);
  }
  if (q.type === 'multiple') {
    const ans = norm(q.answer).split('').sort().join('');
    return userAnswer.split('').sort().join('') === ans;
  }
  return norm(userAnswer) === norm(q.answer);
}

export const isMobile = () => window.innerWidth <= 640;

/** 本地日期 YYYY-MM-DD（避免 toISOString 的 UTC 时区偏移） */
export function localDate(d = new Date()) {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 将答错的题目打包为与导入格式一致的题库对象；无错题时返回 null */
export function buildWrongBank(bank: Bank, answers: Answers): Bank | null {
  const wrong = bank.questions.filter(q => answers.get(q.id)?.ok === false);
  if (wrong.length === 0) return null;

  const byType: Record<string, number> = {};
  const byChapter = new Map<string, number>();
  for (const q of wrong) {
    byType[q.type] = (byType[q.type] || 0) + 1;
    const ch = q.chapter || '未分类';
    byChapter.set(ch, (byChapter.get(ch) || 0) + 1);
  }

  // 仅保留有内容的字段，输出结构与导入的题库文件保持一致（填空题无 options）
  const questions = wrong.map(q => {
    const item: Omit<Question, 'options'> & { options?: Record<string, string> } = {
      id: q.id, type: q.type, question: q.question, answer: q.answer,
    };
    if (q.chapter) item.chapter = q.chapter;
    if (q.point) item.point = q.point;
    if (q.options && Object.keys(q.options).length > 0) item.options = q.options;
    if (q.explanation) item.explanation = q.explanation;
    return item;
  });

  return {
    subject: `${bank.subject || '题库'}-错题`,
    source: bank.subject || bank.source || '',
    generated_at: localDate(),
    total: wrong.length,
    by_type: byType as Bank['by_type'],
    by_chapter: [...byChapter].map(([chapter, count]) => ({ chapter, count })),
    questions: questions as Bank['questions'],
  };
}

/** 触发浏览器下载 JSON 文件 */
export function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * iOS 独立运行（添加到主屏幕）时的视口与安全区兜底，需在应用渲染前调用。
 *
 * 已知的 WebKit 回归（iOS 18.6 / 26.x，bugs.webkit.org 301994 等）：
 * 1. env(safe-area-inset-top) 返回 0：black-translucent 状态栏下内容延伸到状态栏底下，
 *    但 CSS 拿不到安全区高度，顶栏被状态栏/灵动岛遮挡。此 bug 与视口高度谎言互斥
 *    （谎言的差值恰好等于 env-top）。
 * 2. 冷启动时 100dvh/100svh 比真实屏幕矮一个状态栏高度，而 100vh 是正确的。
 */
export function setupMobileViewport() {
  const doc = document.documentElement;
  const standalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  function apply() {
    // 用探针元素测量 env(safe-area-inset-top) 的实际计算值
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;height:env(safe-area-inset-top,0px)';
    document.body.appendChild(probe);
    const measured = probe.getBoundingClientRect().height;
    probe.remove();

    const long = Math.max(window.screen.width, window.screen.height); // 竖屏屏高
    const iPhone = /iPhone|iPod/i.test(window.navigator.userAgent);
    const portrait = window.innerHeight >= window.innerWidth;
    // 视口是否几乎占满整块屏幕（无浏览器工具栏、系统栏也未让出空间）
    const fullViewport = window.innerHeight > window.screen.height - 40;
    // 视口明显矮于屏幕（浏览器工具栏占位，或独立模式冷启动的高度谎言）
    const shortViewport = window.innerHeight < window.screen.height - 40;

    // 修复 1：独立模式冷启动时 dvh 说谎（此时 env 正常），改用 100vh 撑满真实屏幕，
    // 溢出会触发系统重算视口，重算后本条件不再成立、自动回到 dvh。
    if (standalone() && measured > 0 && shortViewport) {
      doc.style.setProperty('--app-height', '100vh');
    } else {
      doc.style.removeProperty('--app-height');
    }

    // 修复 2：env-top 为 0 且视口占满整屏（仅剩“状态栏悬浮在内容上”这一种可能）时，
    // 按机型估算安全区兜底。屏幕高度（CSS px）：灵动岛/刘海机型 ≥ 812，SE 为 667。
    if (measured === 0 && iPhone && portrait && fullViewport) {
      doc.style.setProperty('--safe-top', long >= 800 ? '59px' : long >= 780 ? '47px' : '20px');
      doc.style.setProperty('--safe-bottom', long >= 700 ? '34px' : '0px');
    } else {
      doc.style.removeProperty('--safe-top');
      doc.style.removeProperty('--safe-bottom');
    }
  }

  apply();
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
}
